"use client";

import React, { useEffect, useContext, useRef } from "react";
import { loadingContext } from "../../store/context/isLoadingContext";
import { screenSizeContext } from "../../store/context/screenSizesContext";

const Loader = () => {
	const { isIpad, isReady } = useContext(screenSizeContext);
	const { isPageFetched, setIsPageFetched } = useContext(loadingContext);
	const whiteLayerRef = useRef<HTMLDivElement>(null);

	const handlePopState = () => {
		setIsPageFetched("loading");
		tl.current?.seek("start");
	};
	const getInitialConfig = () => {
    if (typeof window === 'undefined') {
			return {
				dpr: 1,
				width: 0,
				height: 0,
				center: { x: 0, y: 0 },
				ctx: null,
				label: "start"
			};
    }
    
    return {
			dpr: window.devicePixelRatio,
			width: window.innerWidth,
			height: window.innerHeight,
			center: { x: window.innerWidth / 2, y: window.innerHeight / 2 },
			ctx: null,
			label: "start"
    };
	};
	const checkIsBooking = () => {
		const params = new URLSearchParams(window.location.search);
		return params.has("book");
	};

	const canvas = useRef<HTMLCanvasElement>(null);
	const tl = useRef<GSAPTimeline>(null);
	const config = useRef<{
		dpr: number, 
		width: number, 
		height: number,
		center: {x: number, y: number},
		ctx: CanvasRenderingContext2D | null
		label: string
	}> (getInitialConfig());
	const tweaks = useRef<{
		outer: {height: number, aspect: number, scale: number},
		inner: {height: number, width: number, aspect: number, scaleX: number, scaleY: number}
		canvas: {scale: number}
	}> ({ 
		outer: {height: 897, aspect: 0.9754738016, scale: 0}, // 0.25
		inner: {width: 604, height: 778.774, aspect: 0, scaleX: 0, scaleY: 0}, // 0.55
		canvas: {scale: 1}
	});

	const shouldDraw = useRef(true);

	const resumeCanvasDraw = () => {
		shouldDraw.current = true;
		if (canvas.current) canvas.current.style.visibility = "visible";
	};

	const pauseCanvasDraw = () => {
		shouldDraw.current = false;
		if (canvas.current) {
			canvas.current.style.visibility = "hidden";
			config.current.ctx?.clearRect(0, 0, config.current.width, config.current.height);
		}
	};

	useEffect(() => {
		// Timeline reads isIpad for scroll unlock — wait until viewport is known.
		if (!isReady) return;

		let cancelled = false;
		let cleanup: (() => void) | undefined;

		void (async () => {
			const { default: gsap } = await import("gsap");
			if (cancelled) return;

		if (canvas.current) config.current.ctx = canvas.current.getContext("2d");

		const handleResize = () => {
			config.current.dpr = window.devicePixelRatio;
			config.current.width = window.innerWidth;
			config.current.height = window.innerHeight;
			config.current.center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };

			if (canvas.current) {
				canvas.current.width = config.current.width;
				canvas.current.height = config.current.height;
				canvas.current.style.width = "100%";
				canvas.current.style.height = "100%";
			}
		};

		const handleUpdate = () => {
			if (!shouldDraw.current || !config.current.ctx) return;

			config.current.ctx.clearRect(0, 0, config.current.width, config.current.height);

			config.current.ctx.rect(0, 0, config.current.width, config.current.height);
			config.current.ctx.fillStyle = "#fff";
			config.current.ctx.fill();

			config.current.ctx.save();
			config.current.ctx.globalCompositeOperation = "destination-out";
			config.current.ctx.beginPath();
			config.current.ctx.ellipse(
				config.current.center.x,
				config.current.center.y,
				tweaks.current.outer.height * tweaks.current.outer.scale * tweaks.current.canvas.scale,
				tweaks.current.outer.height * tweaks.current.outer.aspect * tweaks.current.outer.scale * tweaks.current.canvas.scale,
				Math.PI / 2,
				0,
				2 * Math.PI
			);
			config.current.ctx.closePath();
			config.current.ctx.fill();
			config.current.ctx.restore();

			config.current.ctx.save();
			config.current.ctx.translate(
				config.current.center.x - (tweaks.current.inner.width / 2) * tweaks.current.inner.scaleX,
				config.current.center.y - (tweaks.current.inner.height / 2) * tweaks.current.inner.scaleY
			);
			config.current.ctx.scale(tweaks.current.inner.scaleX, tweaks.current.inner.scaleY);
			config.current.ctx.beginPath();
			config.current.ctx.moveTo(301.165, 779.774);
			config.current.ctx.bezierCurveTo(353.824, 779.774, 399.333, 769.821, 437.692, 749.841);
			config.current.ctx.bezierCurveTo(476.05, 729.935, 507.482, 702.602, 531.912, 668.064);
			config.current.ctx.bezierCurveTo(556.342, 633.452, 574.442, 592.452, 586.061, 545.065);
			config.current.ctx.bezierCurveTo(597.68, 497.677, 603.564, 446.13, 603.564, 390.424);
			config.current.ctx.bezierCurveTo(603.564, 334.718, 597.755, 283.245, 586.061, 235.783);
			config.current.ctx.bezierCurveTo(574.367, 188.396, 556.342, 147.173, 531.912, 112.19);
			config.current.ctx.bezierCurveTo(507.482, 77.2062, 476.05, 49.9473, 437.692, 30.3387);
			config.current.ctx.bezierCurveTo(399.333, 10.8043, 353.824, 1, 301.165, 1);
			config.current.ctx.bezierCurveTo(248.506, 1, 203.369, 10.8043, 165.756, 30.3387);
			config.current.ctx.bezierCurveTo(128.142, 49.9473, 97.0826, 77.2062, 72.6523, 112.19);
			config.current.ctx.bezierCurveTo(48.222, 147.173, 30.1227, 188.322, 18.5034, 235.783);
			config.current.ctx.bezierCurveTo(6.80964, 283.171, 1, 334.718, 1, 390.424);
			config.current.ctx.bezierCurveTo(1, 446.13, 7.03308, 497.677, 19.0248, 545.065);
			config.current.ctx.bezierCurveTo(31.0165, 592.452, 49.4882, 633.452, 74.2909, 668.064);
			config.current.ctx.bezierCurveTo(99.0936, 702.676, 130.302, 729.935, 167.99, 749.841);
			config.current.ctx.bezierCurveTo(205.604, 769.747, 249.995, 779.774, 301.165, 779.774);
			config.current.ctx.closePath();
			config.current.ctx.fillStyle = "#fff";
			config.current.ctx.fill();
			config.current.ctx.restore();
		};

		if (!tl.current) {
			tl.current = gsap.timeline({ paused: true });

			const centerLetters = gsap.to(".back-white .letter-center", {
				paused: true,
				x: "0%",
				y: "0%",
				alpha: 1,
				scale: 1,
				force3D: true,
			});
			const cornerLetters = gsap.to(".back-white .letter-corner", {
				paused: true,
				x: "0%",
				y: "0%",
				alpha: 1,
				scale: 1,
				force3D: true,
			});

			tl.current
				?.to(tweaks.current.outer, {
					duration: 1.2,
					ease: "expo.inOut",
					scale: isIpad ? 0.075 : 0.09,
					onStart: () => {
						resumeCanvasDraw();
						if (whiteLayerRef.current) whiteLayerRef.current.style.display = "none";
					},
				})
				.set(tweaks.current.inner, { scaleY: (isIpad ? 0.07 : 0.085) * 2.2 }, "-=0.6")
				.to(
					tweaks.current.inner,
					{
						duration: 0.9,
						ease: "expo.inOut",
						scaleX: (isIpad ? 0.075 : 0.085) * 2.2,
					},
					"<"
				)
				.addLabel("start")
				.to(
					centerLetters,
					{
						duration: 0.8,
						ease: "power3.inOut",
						progress: 1,
					},
					"-=0.6"
				)
				.to(
					cornerLetters,
					{
						duration: 0.8,
						ease: "power3.inOut",
						progress: 1,
					},
					"-=0.75"
				)
				.to(cornerLetters, {
					duration: 0.8,
					ease: "power3.inOut",
					progress: 0,
					onStart: () => {
						if (isIpad) window.scrollTo(0, 0);
						else window.lenis?.scrollTo(0, { immediate: true, force: true });
						gsap.to(".blur-layer", { duration: 0.3, opacity: 0, ease: "power2.inOut" });
					},
					onReverseComplete: () => {
						gsap.to(".blur-layer", { duration: 0.3, opacity: 1, ease: "power2.inOut" });
					},
				})
				.to(
					centerLetters,
					{
						duration: 0.8,
						ease: "power3.inOut",
						progress: 0,
					},
					"-=0.75"
				)
				.to(
					tweaks.current.outer,
					{
						duration: 1.2,
						ease: "expo.inOut",
						scale: 0.25,
					},
					"-=0.5"
				)
				.to(
					tweaks.current.inner,
					{
						duration: 1.2,
						ease: "expo.inOut",
						scaleY: 0.25 * 2.2,
					},
					"<"
				)
				.to(
					tweaks.current.inner,
					{
						duration: 0.9,
						ease: "expo.inOut",
						scaleX: 0,
					},
					"-=1.05"
				)
				.to(
					tweaks.current.canvas,
					{
						duration: 2,
						ease: "expo.inOut",
						scale: isIpad
							? Math.max(7.5, 7.5 * (window.innerHeight / 932))
							: Math.max(5.5, 5.5 * (window.innerWidth / 1920)),
						onComplete: () => {
							if (!checkIsBooking()) {
								if (!isIpad) window.lenis?.startScroll?.(true);
								else document.documentElement.style.overflow = "auto";
							}
						},
					},
					"-=0.6"
				)
				.addLabel("end")
				.call(() => {
					const html = document.documentElement;
					if (!html.classList.contains("loaded")) html.classList.add("loaded");
					pauseCanvasDraw();
				});
		}

		handleResize();
		const handleUpdateTicker = gsap.ticker.add(handleUpdate);

		const whiteLayerTimer = setTimeout(() => {
			if (whiteLayerRef.current) whiteLayerRef.current.style.display = "none";
		}, 50);

		window.addEventListener("resize", handleResize);
		window.addEventListener("popstate", handlePopState);

		// Page may already be "fetched" before the async timeline finished building.
		if (isPageFetched === "fetched" && tl.current && !document.documentElement.classList.contains("loaded")) {
			resumeCanvasDraw();
			tl.current.play();
		}

		cleanup = () => {
			clearTimeout(whiteLayerTimer);
			gsap.ticker.remove(handleUpdateTicker);
			tl.current?.kill();
			tl.current = null;
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("popstate", handlePopState);
		};
		})();

		return () => {
			cancelled = true;
			cleanup?.();
		};
		// Init once viewport is known — isIpad is correct for scroll unlock on mobile.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isReady]);

	useEffect(() => {
		if (!isReady || !tl.current) return;

		const html = document.documentElement;

		if (html.classList.contains("loaded")) {
			if (isPageFetched === "fetched") {
				config.current.label = "end";
				resumeCanvasDraw();
				tl.current.timeScale(1).tweenFromTo("start", "end");
			} else if (isPageFetched === "loading") {
				config.current.label = "start";
				resumeCanvasDraw();
				tl.current.timeScale(1.25).tweenTo("start");
			}
		} else if (isPageFetched === "fetched") {
			resumeCanvasDraw();
			tl.current.play();
		}
	}, [isPageFetched, isReady]);

	return (
		<section className="pre-loader || fixed top-0 left-0 w-screen h-screen z-[999] pointer-events-none">
			<div className="blur-layer || absolute top-0 left-0 w-full h-full bg-[#0d223d] -z-10"></div>
			<canvas ref={canvas}></canvas>
			<div className="back-white || absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
				<div className="letter letter-corner">
					<span>W</span>
				</div>
				<div className="letter letter-center">
					<span>E</span>
				</div>
				<div className="letter letter-center">
					<span>S</span>
				</div>
				<div className="letter letter-corner">
					<span>T</span>
				</div>
			</div>
			<div ref={whiteLayerRef} className="placeholder_whitelayer absolute top-0 left-0 w-screen h-screen bg-white"></div>
		</section>
	)
};

export default Loader;